import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { backupLogAPI } from '../api/backupLog';
import { toast } from 'react-toastify';
import './BackupLogDetails.css';

const BackupLogDetails = () => {
  const { logId } = useParams();
  const navigate = useNavigate();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogDetails();
  }, [logId]);

  const fetchLogDetails = async () => {
    try {
      setLoading(true);
      const response = await backupLogAPI.getLogById(logId);
      setLog(response.data);
    } catch (error) {
      toast.error('Failed to fetch backup log details');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(2)} GB`;
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`;
    return `${(bytes / 1024).toFixed(2)} KB`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  if (loading) {
    return <div className="loading">Loading backup log details...</div>;
  }

  if (!log) {
    return <div className="error">Backup log not found</div>;
  }

  return (
    <div className="backup-log-details-page">
      <button onClick={() => navigate(-1)} className="btn-back">
        ← Back
      </button>

      <div className="log-header">
        <h1>Backup Log Details</h1>
        <span className={`status-badge status-${log.status}`}>{log.status}</span>
      </div>

      <div className="details-grid">
        {/* Basic Info */}
        <section className="detail-section">
          <h2>Basic Information</h2>
          <div className="detail-row">
            <span>Configuration:</span>
            <strong>{log.backupConfig?.configName}</strong>
          </div>
          <div className="detail-row">
            <span>Installation:</span>
            <strong>{log.installation?.installationName}</strong>
          </div>
          <div className="detail-row">
            <span>Backup Type:</span>
            <strong>{log.backupType}</strong>
          </div>
          <div className="detail-row">
            <span>Schedule:</span>
            <strong>{log.scheduleName || 'Manual'}</strong>
          </div>
        </section>

        {/* Timing */}
        <section className="detail-section">
          <h2>Timing</h2>
          <div className="detail-row">
            <span>Start Time:</span>
            <strong>{new Date(log.startTime).toLocaleString()}</strong>
          </div>
          {log.endTime && (
            <div className="detail-row">
              <span>End Time:</span>
              <strong>{new Date(log.endTime).toLocaleString()}</strong>
            </div>
          )}
          <div className="detail-row">
            <span>Duration:</span>
            <strong>{formatDuration(log.duration)}</strong>
          </div>
        </section>

        {/* Database Info */}
        {log.database && (
          <section className="detail-section">
            <h2>Database</h2>
            <div className="detail-row">
              <span>Name:</span>
              <strong>{log.database.name}</strong>
            </div>
            <div className="detail-row">
              <span>Size:</span>
              <strong>{formatBytes(log.database.sizeBytes)}</strong>
            </div>
          </section>
        )}

        {/* Backup File */}
        {log.backupFile && (
          <section className="detail-section">
            <h2>Backup File</h2>
            <div className="detail-row">
              <span>Name:</span>
              <strong>{log.backupFile.name}</strong>
            </div>
            <div className="detail-row">
              <span>Size:</span>
              <strong>{formatBytes(log.backupFile.sizeBytes)}</strong>
            </div>
            <div className="detail-row">
              <span>Path:</span>
              <code>{log.backupFile.path}</code>
            </div>
            {log.backupFile.checksum && (
              <div className="detail-row">
                <span>Checksum:</span>
                <code>{log.backupFile.checksum}</code>
              </div>
            )}
          </section>
        )}

        {/* SFTP Results */}
        {log.sftpResults && log.sftpResults.length > 0 && (
          <section className="detail-section full-width">
            <h2>SFTP Upload Results</h2>
            <div className="sftp-results">
              {log.sftpResults.map((sftp, idx) => (
                <div key={idx} className={`sftp-result sftp-${sftp.status}`}>
                  <div className="sftp-header">
                    <strong>{sftp.serverName}</strong>
                    <span className={`sftp-status ${sftp.status}`}>{sftp.status}</span>
                  </div>
                  <div className="sftp-details">
                    <div>Host: {sftp.host}</div>
                    {sftp.uploadedAt && (
                      <div>Uploaded: {new Date(sftp.uploadedAt).toLocaleString()}</div>
                    )}
                    {sftp.uploadDuration && (
                      <div>Duration: {formatDuration(sftp.uploadDuration)}</div>
                    )}
                    {sftp.uploadSpeedMbps && (
                      <div>Speed: {sftp.uploadSpeedMbps.toFixed(2)} Mbps</div>
                    )}
                    {sftp.error && <div className="error-text">Error: {sftp.error}</div>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Performance Metrics */}
        {log.metrics && (
          <section className="detail-section">
            <h2>Performance Metrics</h2>
            {log.metrics.cpuUsagePercent && (
              <div className="detail-row">
                <span>CPU Usage:</span>
                <strong>{log.metrics.cpuUsagePercent}%</strong>
              </div>
            )}
            {log.metrics.memoryUsageMB && (
              <div className="detail-row">
                <span>Memory Usage:</span>
                <strong>{log.metrics.memoryUsageMB} MB</strong>
              </div>
            )}
            {log.metrics.compressionRatio && (
              <div className="detail-row">
                <span>Compression Ratio:</span>
                <strong>{(log.metrics.compressionRatio * 100).toFixed(1)}%</strong>
              </div>
            )}
          </section>
        )}

        {/* Error Details */}
        {log.error && (
          <section className="detail-section full-width error-section">
            <h2>Error Details</h2>
            <div className="error-details">
              <div className="detail-row">
                <span>Message:</span>
                <strong className="error-text">{log.error.message}</strong>
              </div>
              {log.error.code && (
                <div className="detail-row">
                  <span>Code:</span>
                  <code>{log.error.code}</code>
                </div>
              )}
              {log.error.details && (
                <div className="detail-row">
                  <span>Details:</span>
                  <div>{log.error.details}</div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Warnings */}
        {log.warnings && log.warnings.length > 0 && (
          <section className="detail-section full-width">
            <h2>Warnings</h2>
            <div className="warnings-list">
              {log.warnings.map((warning, idx) => (
                <div key={idx} className="warning-item">
                  <span className="warning-time">
                    {new Date(warning.timestamp).toLocaleString()}
                  </span>
                  <span className="warning-message">{warning.message}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Metadata */}
        {log.metadata && (
          <section className="detail-section">
            <h2>System Information</h2>
            {log.metadata.computerName && (
              <div className="detail-row">
                <span>Computer:</span>
                <strong>{log.metadata.computerName}</strong>
              </div>
            )}
            {log.metadata.softwareVersion && (
              <div className="detail-row">
                <span>Software Version:</span>
                <strong>{log.metadata.softwareVersion}</strong>
              </div>
            )}
            {log.metadata.ipAddress && (
              <div className="detail-row">
                <span>IP Address:</span>
                <code>{log.metadata.ipAddress}</code>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default BackupLogDetails;